/**
 * QuotedSelection — the "highlight and ask" quote: the text the writer
 * selected in Word, shown as a small quote card (mono caption + a
 * clamped excerpt behind a violet rule).
 *
 * One grammar for both ends of the flow so the writer recognises the
 * same object twice:
 *   - composer: SelectionContextPill wraps it with a dismiss button
 *     ("this will be sent with your next message")
 *   - thread:   MessageBubble renders it above the sent user bubble
 *     ("this is what the question was about")
 *
 * SRP: pure presentation. The caller decides the caption, the line
 * clamp and any trailing control.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

export interface QuotedSelectionProps {
  /** The selected text (full; the card clamps it visually). */
  text: string;
  /** Mono caption above the excerpt, e.g. "Selected in Word". */
  caption: string;
  /** Max visible lines of the excerpt. Default 2. */
  lines?: number;
  /** Optional trailing control on the caption row (e.g. a dismiss ×). */
  trailing?: React.ReactNode;
  /** Optional className for the outer card (layout from the caller). */
  className?: string;
  /** Accessible label for the card. */
  ariaLabel?: string;
}

const useStyles = makeStyles({
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "3px",
    minWidth: 0,
    padding: "6px 8px 7px 10px",
    background: brandColors.tint,
    border: `1px solid ${surfaceColors.line}`,
    borderLeft: `2px solid ${brandColors.primary}`,
    borderRadius: "8px",
    boxSizing: "border-box",
  },
  captionRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minHeight: "14px",
  },
  caption: {
    flex: 1,
    minWidth: 0,
    fontFamily: fonts.mono,
    fontSize: "9.5px",
    fontWeight: 600,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: brandColors.primary,
    userSelect: "none",
  },
  excerpt: {
    fontFamily: fonts.body,
    fontSize: "12px",
    lineHeight: 1.45,
    fontStyle: "italic",
    color: inkColors.ink3,
    textAlign: "left",
    wordBreak: "break-word",
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
});

export const QuotedSelection: React.FC<QuotedSelectionProps> = ({
  text,
  caption,
  lines = 2,
  trailing,
  className,
  ariaLabel,
}) => {
  const styles = useStyles();
  return (
    <div className={mergeClasses(styles.card, className)} role="note" aria-label={ariaLabel} title={text}>
      <div className={styles.captionRow}>
        <span className={styles.caption}>{caption}</span>
        {trailing ?? null}
      </div>
      {/* Line clamp is a per-instance number, so it rides inline. */}
      <div className={styles.excerpt} style={{ WebkitLineClamp: lines }}>
        {`“${text}”`}
      </div>
    </div>
  );
};

export default QuotedSelection;
